import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import { PAGE_CONTENT_CLASS } from '../../components/layout/PageContent';
import Toast from '../../components/ui/Toast';
import LoadingOverlay from '../../components/ui/LoadingOverlay';
import LoadingMark from '../../components/ui/LoadingMark';
import Button from '../../components/ui/Button';

import TemplateMetrics from './components/TemplateMetrics';
import TemplateFilters from './components/TemplateFilters';
import TemplateTable from './components/TemplateTable';
import TemplateCardMobile from './components/TemplateCardMobile';
import EmptyState from './components/EmptyState';
import TemplateDeleteDialog from './components/TemplateDeleteDialog';

import DownloadDocumentOverlay from '../contract-dashboard/components/DownloadDocumentOverlay';
import userService from '../../services/userService';
import {
  listUserTemplates,
  deleteUserTemplate,
  duplicateUserTemplate,
  downloadTemplateDocx,
} from '../../services/templateService';
import { FavoriteModelsProvider } from '../../context/FavoriteModelsContext';

const LIST_PAGE_SIZE = 20;

function formatToastMessage(message) {
  if (message == null) return 'Ocorreu um erro.';
  if (typeof message === 'string') return message;
  if (typeof message === 'object' && typeof message.message === 'string') {
    return message.message;
  }
  return 'Ocorreu um erro.';
}

function compareTemplatesForSort(a, b, sortKey, sortDir) {
  const mul = sortDir === 'asc' ? 1 : -1;

  switch (sortKey) {
    case 'name': {
      const va = (a.name || '').toLowerCase();
      const vb = (b.name || '').toLowerCase();
      return mul * va.localeCompare(vb, 'pt-BR');
    }
    case 'createdAt': {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return mul * (da - db);
    }
    default:
      return 0;
  }
}

const LoadingState = () => (
  <div className="flex justify-center py-20">
    <LoadingMark message="Carregando modelos..." />
  </div>
);

const UserTemplatesPage = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [visibleCount, setVisibleCount] = useState(LIST_PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(true);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [toast, setToast] = useState(null);

  const dismissToast = useCallback(() => {
    setToast(null);
  }, []);

  const showToast = useCallback((type, message) => {
    const normalizedMessage =
      typeof message === 'string'
        ? message
        : formatToastMessage(message);

    setToast({ type, message: normalizedMessage });
  }, []);

  const loadUser = async () => {
    try {
      const result = await userService.getUserInfo();
      if (result?.success && result?.data) {
        setUser(result.data);
      }
    } catch (error) {
      console.error('Error loading user info:', error);
    }
  };

  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await listUserTemplates();
      if (!result.success) {
        showToast(
          'error',
          typeof result.error === 'string'
            ? result.error
            : 'Erro ao carregar modelos.'
        );
        setTemplates([]);
        setVisibleCount(LIST_PAGE_SIZE);
        return;
      }

      setTemplates(result.data?.items ?? []);
      setVisibleCount(LIST_PAGE_SIZE);
    } catch (error) {
      console.error('Error loading templates:', error);
      showToast('error', 'Erro ao carregar modelos.');
      setTemplates([]);
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadUser();
    loadTemplates();
  }, [loadTemplates]);

  const filteredTemplates = useMemo(() => {
    if (!search.trim()) return templates;

    const term = search.trim().toLowerCase();
    return templates.filter((template) =>
      template.name?.toLowerCase().includes(term)
    );
  }, [templates, search]);

  const sortedTemplates = useMemo(() => {
    if (!sortKey) return filteredTemplates;
    return [...filteredTemplates].sort((a, b) =>
      compareTemplatesForSort(a, b, sortKey, sortDir)
    );
  }, [filteredTemplates, sortKey, sortDir]);

  const visibleTemplates = useMemo(
    () => sortedTemplates.slice(0, visibleCount),
    [sortedTemplates, visibleCount]
  );

  const showLoadMore =
    sortedTemplates.length > LIST_PAGE_SIZE &&
    visibleCount < sortedTemplates.length;

  useEffect(() => {
    setVisibleCount(LIST_PAGE_SIZE);
  }, [search]);

  const handleSortColumn = useCallback((key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }, [sortKey]);

  const handleLoadMore = () => {
    setVisibleCount((c) => c + LIST_PAGE_SIZE);
  };

  const handleEditTemplate = (template) => {
    if (!template?.template_id) {
      showToast('error', 'Não foi possível abrir o editor: modelo sem identificador.');
      return;
    }
    navigate(`/edit-template?template_id=${encodeURIComponent(String(template.template_id))}`);
  };

  const handleCreateContract = (template) => {
    const templateId = template?.template_id ?? template?.id;
    if (!templateId) {
      showToast('error', 'Não foi possível gerar documento: modelo sem identificador.');
      return;
    }
    navigate(
      `/contract-generator?user_template_id=${encodeURIComponent(String(templateId))}`
    );
  };

  const handleRequestDelete = (template) => {
    setTemplateToDelete(template);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setTemplateToDelete(null);
  }, []);

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    const templateId = templateToDelete.template_id ?? templateToDelete.id;
    if (!templateId) {
      showToast('error', 'Não foi possível excluir: modelo sem identificador.');
      return;
    }

    try {
      setIsDeleting(true);
      const result = await deleteUserTemplate(templateId);

      if (!result.success) {
        showToast(
          'error',
          typeof result.error === 'string'
            ? result.error
            : 'Erro ao excluir modelo.'
        );
        return;
      }

      setTemplates((prev) =>
        prev.filter((t) => (t.template_id ?? t.id) !== templateId)
      );

      setIsDeleteDialogOpen(false);
      setTemplateToDelete(null);
      showToast('success', 'Modelo excluído com sucesso.');
    } catch (err) {
      console.error('handleConfirmDelete', err);
      showToast('error', 'Erro ao excluir modelo.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadTemplate = async (template) => {
    const templateKey = template?.templateKey ?? template?.template_key;
    if (!templateKey) {
      showToast('error', 'Não foi possível baixar: modelo sem identificador.');
      return;
    }

    try {
      setIsDownloading(true);
      const result = await downloadTemplateDocx(templateKey, template?.name);

      if (!result.success) {
        showToast(
          'error',
          typeof result.error === 'string'
            ? result.error
            : 'Erro ao baixar modelo.'
        );
        return;
      }

      const { blob, fileName } = result.data;
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      showToast('success', 'Download iniciado.');
    } catch (err) {
      console.error('handleDownloadTemplate', err);
      showToast('error', 'Erro ao baixar modelo.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDuplicateTemplate = async (template) => {
    if (!template || isDuplicating) return;

    const templateId = template.template_id ?? template.id;
    if (!templateId) {
      showToast('error', 'Não foi possível duplicar: modelo sem identificador.');
      return;
    }

    setIsDuplicating(true);

    try {
      const result = await duplicateUserTemplate(templateId);

      if (!result.success) {
        showToast(
          'error',
          typeof result.error === 'string'
            ? result.error
            : 'Erro ao duplicar modelo.'
        );
        return;
      }

      setTemplates((prev) => [result.data, ...prev]);
      showToast('success', 'Modelo duplicado com sucesso.');
    } catch (err) {
      console.error('handleDuplicateTemplate', err);
      showToast('error', 'Erro ao duplicar modelo.');
    } finally {
      setIsDuplicating(false);
    }
  };

  return (
    <FavoriteModelsProvider>
      <div className={PAGE_CONTENT_CLASS}>
        <Helmet>
          <title>Meus Modelos | neo-modelos</title>
        </Helmet>

        <TemplateMetrics user={user} templateCount={templates.length} />

        <TemplateFilters search={search} onSearchChange={setSearch} />

        {isLoading ? (
          <LoadingState />
        ) : filteredTemplates.length === 0 ? (
          <EmptyState hasSearchFilter={Boolean(search.trim())} />
        ) : (
          <>
            <div className="hidden md:block">
              <TemplateTable
                templates={visibleTemplates}
                sortKey={sortKey}
                sortDir={sortDir}
                onSortColumn={handleSortColumn}
                onEdit={handleEditTemplate}
                onCreateContract={handleCreateContract}
                onDelete={handleRequestDelete}
                onDuplicate={handleDuplicateTemplate}
                onDownload={handleDownloadTemplate}
                actionsDisabled={isDuplicating || isDownloading}
              />
            </div>

            <div className="md:hidden space-y-4">
              {visibleTemplates.map((template) => (
                <TemplateCardMobile
                  key={template.template_id}
                  template={template}
                  onEdit={handleEditTemplate}
                  onCreateContract={handleCreateContract}
                  onDelete={handleRequestDelete}
                  onDuplicate={handleDuplicateTemplate}
                  onDownload={handleDownloadTemplate}
                  actionsDisabled={isDuplicating || isDownloading}
                />
              ))}
            </div>

            {showLoadMore ? (
              <div className="flex justify-center mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleLoadMore}
                  className="min-w-[140px]"
                >
                  Ver Mais
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>

      <TemplateDeleteDialog
        open={isDeleteDialogOpen}
        templateName={templateToDelete?.name ?? ''}
        isDeleting={isDeleting}
        onCancel={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
      />

      {isDuplicating ? <LoadingOverlay message="Duplicando modelo..." /> : null}

      <DownloadDocumentOverlay visible={isDownloading} message="Baixando modelo..." />

      <Toast toast={toast} onDismiss={dismissToast} />
    </FavoriteModelsProvider>
  );
};

export default UserTemplatesPage;
